/**
 * Fetching the menu.
 *
 * Specification: ../test/api-client.spec.ts. Master plan §4 M1, §2 D1.
 *
 * The menu arrives pre-taxed: the server resolved every rate against the rule
 * registry, so the till never has to, which is what makes ringing during an
 * outage ordinary rather than degraded.
 *
 * MONEY ARRIVES AS DECIMAL STRINGS and is parsed to bigint here. `Number` would
 * be a float on the money path — D1's one prohibition — and it would be
 * invisible, because every plausible test amount round-trips through a double
 * without complaint.
 *
 * A FAILED FETCH RAISES. It does not return an empty menu: an empty menu is
 * indistinguishable from a venue nobody has stocked, and the till would quietly
 * offer nothing instead of keeping the menu it already has. Whether to keep the
 * cached one is the caller's decision, and this layer must not make it by
 * handing back a plausible-looking blank.
 */

import type { MenuItemSnapshot, MenuSnapshot } from "./session";
import type { CategorySnapshot, ModifierGroupSnapshot } from "@gf/module-catalog/choices";
import type { ConsumptionMode, PricedLine } from "@gf/module-orders";
import { ALLERGENS, type AllergenCode } from "@gf/contracts/values";

export class MenuFetchError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "MenuFetchError";
  }
}

export interface FetchMenuArgs {
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly venueId: string;
  /** Injected so the parsing is testable without a server. */
  readonly fetch?: typeof fetch;
}

interface WirePriced {
  grossMinor: string;
  vatMinor: string;
  netMinor: string;
  rateBp: number;
  taxSourceRef: string;
}

interface WireItem {
  itemRef: string;
  name: string;
  stationId: string;
  allergens?: readonly string[];
  ageRestricted?: boolean;
  byMode: Record<string, WirePriced>;
  categoryId?: string | null;
  description?: string | null;
  modifierGroups?: unknown;
}

interface WireMenu {
  version: number;
  resolvedAt: string;
  categories?: unknown;
  items: WireItem[];
}

const INTEGER = /^-?\d+$/;

function minor(value: unknown, field: string): bigint {
  if (typeof value !== "string" || !INTEGER.test(value)) {
    throw new MenuFetchError(
      `${field} must be a decimal string of minor units, got ${JSON.stringify(value)}`,
    );
  }
  return BigInt(value);
}

function priced(raw: unknown, where: string): PricedLine {
  if (typeof raw !== "object" || raw === null) {
    throw new MenuFetchError(`${where} is not an object`);
  }
  const p = raw as Record<string, unknown>;
  const grossMinor = minor(p["grossMinor"], `${where}.grossMinor`);
  const vatMinor = minor(p["vatMinor"], `${where}.vatMinor`);
  const netMinor = minor(p["netMinor"], `${where}.netMinor`);

  if (typeof p["rateBp"] !== "number") {
    throw new MenuFetchError(`${where}.rateBp must be a number`);
  }

  return {
    grossMinor,
    vatMinor,
    netMinor,
    rateBp: p["rateBp"] as PricedLine["rateBp"],
    taxSourceRef: String(p["taxSourceRef"] ?? ""),
  };
}

/** Annex II, the one closed list. Absent is empty; unrecognised is a refusal. */
const allergenCodes = (raw: unknown, where: string): readonly AllergenCode[] => {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) throw new MenuFetchError(`${where} must be an array`);

  for (const code of raw) {
    if (typeof code !== "string" || !(ALLERGENS as readonly string[]).includes(code)) {
      throw new MenuFetchError(
        `${where} contains ${String(code)}, which is not an Annex II allergen code`,
      );
    }
  }
  return raw as AllergenCode[];
};

const modifierGroups = (raw: unknown, where: string): readonly ModifierGroupSnapshot[] => {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) throw new MenuFetchError(`${where} must be an array`);
  return raw.map((g: Record<string, unknown>, gi) => {
    if (typeof g["groupId"] !== "string" || !Array.isArray(g["options"])) {
      throw new MenuFetchError(`${where}[${gi}] is missing groupId or options`);
    }
    return {
      groupId: g["groupId"],
      name: String(g["name"] ?? ""),
      minSelections: Number(g["minSelections"] ?? 0),
      maxSelections:
        g["maxSelections"] === null || g["maxSelections"] === undefined ? null : Number(g["maxSelections"]),
      options: (g["options"] as Record<string, unknown>[]).map((o, oi) => ({
        optionId: String(o["optionId"] ?? ""),
        name: String(o["name"] ?? ""),
        priceDeltaMinor: minor(o["priceDeltaMinor"], `${where}[${gi}].options[${oi}].priceDeltaMinor`),
        maxQuantity: Number(o["maxQuantity"] ?? 1),
      })),
    };
  });
};

const categories = (raw: unknown): readonly CategorySnapshot[] =>
  Array.isArray(raw)
    ? raw
        .filter((c: Record<string, unknown>) => typeof c["categoryId"] === "string")
        .map((c: Record<string, unknown>) => ({
          categoryId: c["categoryId"] as string,
          name: String(c["name"] ?? ""),
          sortOrder: Number(c["sortOrder"] ?? 0),
        }))
    : [];

/**
 * Fill what a menu cached before categories and modifiers existed does not
 * carry. The version it describes had none of either, so none is the truth.
 */
export function normalizeMenu(menu: MenuSnapshot): MenuSnapshot {
  return {
    ...menu,
    categories: menu.categories ?? [],
    items: menu.items.map((item) => ({
      ...item,
      categoryId: item.categoryId ?? null,
      description: item.description ?? null,
      modifierGroups: item.modifierGroups ?? [],
    })),
  };
}

export async function fetchMenu(args: FetchMenuArgs): Promise<MenuSnapshot> {
  const get = args.fetch ?? fetch;
  const url = `${args.baseUrl}/v1/menu?venueId=${encodeURIComponent(args.venueId)}`;

  let response: Response;
  try {
    response = await get(url, { headers: { authorization: `Bearer ${args.apiKey}` } });
  } catch (error) {
    throw new MenuFetchError("the menu service is unreachable", { cause: error });
  }

  if (!response.ok) {
    throw new MenuFetchError(`the menu service answered ${response.status}`);
  }

  let body: WireMenu;
  try {
    body = (await response.json()) as WireMenu;
  } catch (error) {
    throw new MenuFetchError("the menu service returned something that is not JSON", {
      cause: error,
    });
  }

  if (!Array.isArray(body.items)) {
    throw new MenuFetchError("the menu has no items array");
  }

  const items: MenuItemSnapshot[] = body.items.map((item, index) => {
    if (!item.itemRef || !item.byMode) {
      // Refused rather than skipped. Half a menu is worse than none: staff
      // would look for the item, not find it, and ring something else.
      throw new MenuFetchError(`items[${index}] is missing itemRef or byMode`);
    }

    const byMode: Partial<Record<ConsumptionMode, PricedLine>> = {};
    for (const [mode, raw] of Object.entries(item.byMode)) {
      byMode[mode as ConsumptionMode] = priced(raw, `items[${index}].byMode.${mode}`);
    }

    return {
      itemRef: item.itemRef,
      name: item.name,
      stationId: item.stationId,
      /**
       * VALIDATED AGAINST ANNEX II, not passed through.
       *
       * A code this till does not recognise renders as no allergen at all —
       * a ticket that looks clean on a cook's screen — so an unknown one is a
       * refused menu rather than a quiet omission. Half a menu is worse than
       * none, and a menu with half the allergens is worse than both.
       */
      allergens: allergenCodes(item.allergens, `items[${index}].allergens`),
      /**
       * `=== true`, so anything that is not an explicit yes is a no.
       *
       * The opposite default would be safer in the abstract and wrong here: an
       * older API, or a version published before the field existed, would turn
       * every line on the menu into one the bar cannot pour without a manager.
       * A gate that stops everything is one a venue disables.
       */
      ageRestricted: item.ageRestricted === true,
      byMode,
      categoryId: typeof item.categoryId === "string" ? item.categoryId : null,
      description: typeof item.description === "string" ? item.description : null,
      modifierGroups: modifierGroups(item.modifierGroups, `items[${index}].modifierGroups`),
    };
  });

  return {
    version: body.version,
    resolvedAt: new Date(body.resolvedAt),
    categories: categories(body.categories),
    items,
  };
}

// --- Availability ----------------------------------------------------------

export interface UnavailableItem {
  readonly ref: string;
  readonly reason: string;
  /** When it comes back, already resolved against the venue's clock. */
  readonly until: string;
}

/**
 * What is 86'd right now.
 *
 * A SEPARATE, TINY REQUEST, and that is the whole design rather than an
 * optimisation. A published menu version is frozen so a price cannot move under
 * a check somebody is ringing; availability is the opposite kind of fact — it
 * changes mid-service and has to reach every device in seconds. Folding it into
 * the menu would mean a new immutable version every time a dish ran out, and a
 * till refetching a whole snapshot to learn it.
 *
 * So the till polls this on its own cadence, four times more often than the
 * menu, and merges the two.
 *
 * A FAILED FETCH RAISES, like the menu's. An empty list and an unreachable
 * server are opposite facts: returning `[]` here would put every 86'd dish back
 * on the grid the moment the wifi wobbled, which is the direction that sells
 * food the kitchen does not have.
 */
export async function fetchAvailability(args: FetchMenuArgs): Promise<UnavailableItem[]> {
  const get = args.fetch ?? fetch;
  const url = `${args.baseUrl}/v1/menu/availability?venueId=${encodeURIComponent(args.venueId)}`;

  let response: Response;
  try {
    response = await get(url, { headers: { authorization: `Bearer ${args.apiKey}` } });
  } catch (error) {
    throw new MenuFetchError("the menu service is unreachable", { cause: error });
  }
  if (!response.ok) {
    throw new MenuFetchError(`the menu service answered ${String(response.status)}`);
  }

  const body = (await response.json()) as { unavailable?: unknown };
  if (!Array.isArray(body.unavailable)) {
    throw new MenuFetchError("the availability answer has no `unavailable` array");
  }

  return body.unavailable.flatMap((raw) => {
    const row = raw as Record<string, unknown>;
    /**
     * A ROW WITHOUT A REF IS DROPPED, not defaulted.
     *
     * Every other refusal in this file is total — half a menu is worse than
     * none — and this one is deliberately not. An unparseable availability row
     * would otherwise take the whole list with it and put every 86'd dish back
     * on the grid, which is the failure direction that matters here.
     */
    if (typeof row["ref"] !== "string" || row["ref"].length === 0) return [];
    return [
      {
        ref: row["ref"],
        reason: typeof row["reason"] === "string" ? row["reason"] : "",
        until: typeof row["until"] === "string" ? row["until"] : "",
      },
    ];
  });
}
